<template>
  <div 
    :class="[
      'bg-white rounded-xl shadow-sm border border-gray-200 p-4 transition-all duration-300 relative flex min-w-0 h-full flex-col overflow-hidden',
      clickable ? 'cursor-pointer hover:shadow-lg hover:border-blue-300' : 'cursor-default'
    ]"
    @click="clickable && $emit('click')"
  >
    <!-- Spinner léger -->
    <div
      v-if="props.isLoading"
      class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10 rounded-xl"
    >
      <div class="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-500"></div>
    </div>

    <div class="flex items-start justify-between flex-1 gap-4">
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-black mb-2">{{ title }}</p>
        <p class="text-2xl font-bold text-black mb-1">
          {{ formattedValue }}
          <span v-if="suffix" class="text-xl font-normal text-black ml-1">{{ suffix }}</span>
        </p>
        
        <!-- Change indicator -->
        <div v-if="change !== undefined && change !== null" class="flex flex-wrap items-center mt-3 gap-y-1">
          <div :class="[
            'flex items-center justify-center w-6 h-6 rounded-full mr-2',
            change >= 0 ? 'bg-green-100' : 'bg-red-100'
          ]">
            <i :class="[
              'text-xs',
              change >= 0 ? 'fas fa-arrow-up text-green-600' : 'fas fa-arrow-down text-red-600'
            ]"></i>
          </div>
          <span :class="[
            'text-sm font-semibold',
            change >= 0 ? 'text-green-600' : 'text-red-600'
          ]">
            {{ Math.abs(change) }}%
          </span>
          <span class="text-xs text-black ml-2 font-medium">vs mois dernier</span>
        </div>
      </div>
      
      <!-- Icon -->
      <div :class="[
        'w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0',
        iconBgColor || 'bg-blue-100'
      ]">
        <i :class="[icon, iconColor || 'text-blue-600', 'text-xl leading-none']"></i>
      </div>
    </div>
    
    <!-- Action button -->
    <div v-if="actionLabel" class="mt-3 flex min-w-0 justify-end">
      <button
        @click.stop="$emit('action')"
        class="stats-card-action group inline-flex w-full max-w-full items-center justify-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-center text-sm font-semibold leading-tight text-blue-600 transition-all duration-200 hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-200"
      >
        <span class="min-w-0">{{ actionLabel }}</span>
        <i class="fas fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-0.5"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { StatsCardProps, StatsCardEmits } from '../../types/dashboard';

const props = withDefaults(defineProps<StatsCardProps>(), {
  formatValue: true,
  clickable: true,
  isLoading: false
});

const formattedValue = computed(() => {
  const value = props.value;
  if (!props.formatValue || typeof value === 'string') {
    return value;
  }

  if (props.title?.toLowerCase().includes('chiffre d\'affaires')) {
    return value.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  
  return value.toLocaleString();
});

defineEmits<StatsCardEmits>();
</script>

<style scoped>
</style>
